import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { api } from './api.js';
import { pageStyles } from './components/cssstyles.js';
import { consume, contexts } from './contexts.js';
import { PageNewOrganization } from './page-new-organization.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

@customElement('page-user-organizations')
export class PageUserOrganizations extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [ '/organizations', '/organizations/' ],
      render: () => html`<page-user-organizations></page-user-organizations>`,
    }];
  }

  static url(): string {
    return platformUrl('/organizations');
  }

  @consume({ context: contexts.user, subscribe: true })
  private _user?: ContextType<typeof contexts.user>;

  private _organizationsTask = new Task(this, {
    args: () => [this._user],
    task: async ([user], { signal }) => {
      const result = await api.user.dashboard.GET(undefined, { signal });
      const orgSlugToOrg = new Map<string, WireTypes.Organization>();
      for (const org of result.ownedOrgs)
        orgSlugToOrg.set(org.orgSlug, org);
      for (const org of result.sharedOrgs)
        orgSlugToOrg.set(org.orgSlug, org);
      return [...orgSlugToOrg.values()];
    }
  });

  private _allOrganizationsTask = new Task(this, {
    args: () => [this._user],
    task: async ([user], { signal }) => {
      if (!user?.isSuperUser)
        return undefined;
      return await api.administration.allOrganizations.GET(undefined, { signal });
    }
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'Organizations · Dashboard' }),
  });

  render() {
    return html`
      <user-header submenu=organizations></user-header>
      <app-body medium>
        <h-box>
          <div class=title>Organizations</div>
          <x-filler></x-filler>
          <sl-button
            ?disabled=${!this._user}
            href=${PageNewOrganization.url()}
            variant=success
            size=small>
            <sl-icon slot=prefix name=folder-plus></sl-icon>New organization
          </sl-button>
        </h-box>
        ${this._organizationsTask.render({
          complete: orgs => orgs.length ? html`<list-organizations .orgs=${orgs}></list-organizations>` : html`
            <section class=empty-state>
              <sl-icon class=empty-icon name=buildings></sl-icon>
              <h2>Create your first organization</h2>
              <p>Organize your projects, track test results, and invite your team when you're ready.</p>
              <sl-button href=${PageNewOrganization.url()} variant=success>
                <sl-icon slot=prefix name=folder-plus></sl-icon>Create organization
              </sl-button>
            </section>
          `,
          pending: () => html`<sl-spinner></sl-spinner>`,
        })}
        ${this._user?.isSuperUser ? html`
          <sl-divider></sl-divider>
          <div class=superadm-title>Deployment Organizations</div>
          ${this._allOrganizationsTask.render({
            complete: value => {
              if (!value)
                return nothing;
              const userOrgSlugs = new Set(this._organizationsTask.value?.map(o => o.orgSlug) ?? []);
              const otherOrgs = value.filter(o => !userOrgSlugs.has(o.orgSlug));
              return otherOrgs.length ? html`<list-organizations .orgs=${otherOrgs}></list-organizations>` : nothing;
            },
            pending: () => html`<sl-spinner></sl-spinner>`,
          })}
        ` : nothing}
      </app-body>
      <app-footer></app-footer>
    `;
  }

  static styles = [pageStyles, css`
    .title {
      font-size: var(--sl-font-size-2x-large);
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: var(--sl-spacing-3x-large) var(--sl-spacing-large);
      border: 1px solid var(--sl-panel-border-color);
      border-radius: var(--sl-border-radius-medium);
      text-align: center;

      .empty-icon {
        padding: var(--sl-spacing-large);
        border-radius: var(--sl-border-radius-circle);
        background: var(--sl-color-primary-50);
        color: var(--sl-color-primary-600);
        font-size: var(--sl-font-size-2x-large);
      }

      h2 {
        margin: var(--sl-spacing-large) 0 var(--sl-spacing-small);
        font-size: var(--sl-font-size-x-large);
        font-weight: var(--sl-font-weight-semibold);
      }

      p {
        max-width: 48ch;
        margin: 0 0 var(--sl-spacing-large);
        color: var(--sl-color-neutral-600);
        line-height: var(--sl-line-height-normal);
      }
    }

    .superadm-title {
      font-size: var(--sl-font-size-large);
    }
  `];
}
